import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Button, Card, Col, Row, App, Space, Typography, Tag, 
  Table, Collapse, Descriptions, Image, Spin
} from 'antd';
import { ArrowLeftOutlined, EditOutlined } from '@ant-design/icons';
import { getRoutineById } from '../../services/routines';
import { resolveMedia } from '../../utils/media';
import { getPreparationCatalog } from '../../services/preparations';

const { Text, Title } = Typography;

const RoutineViewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { message } = App.useApp();

  const [loading, setLoading] = useState(true);
  const [routine, setRoutine] = useState(null);
  const [miniRoutinesCatalog, setMiniRoutinesCatalog] = useState([]);

  // Estados de expansión para las secciones
  const [expandedWeeks, setExpandedWeeks] = useState([]);
  const [expandedDays, setExpandedDays] = useState([]);
  const [expandedBlocks, setExpandedBlocks] = useState([]);

  // Cargar datos de la rutina
  useEffect(() => {
    if (!id) return;

    setLoading(true);
    
    // Carga paralela de la rutina y las minirutinas
    Promise.all([
      getRoutineById(id),
      getPreparationCatalog('ALL').catch(() => [])
    ])
      .then(([routineData, miniRoutines]) => {
        setRoutine(routineData);
        setMiniRoutinesCatalog(miniRoutines || []);
      })
      .catch((e) => {
        console.error('Error al cargar la rutina:', e);
        message.error('No se pudo cargar la información de la rutina');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id, message]);

  // Normalización de la estructura de semanas
  const weeks = useMemo(() => {
    if (!routine?.weekly_structure || !Array.isArray(routine.weekly_structure)) {
      return [];
    }
    return routine.weekly_structure;
  }, [routine]);

  const getPanelKey = (...parts) => parts.join('::');

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" tip="Cargando rutina..." />
      </div>
    );
  }

  if (!routine) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Title level={4}>No se encontró la rutina solicitada.</Title>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/routines/all')}>
          Volver al catálogo
        </Button>
      </div>
    );
  }

  const isPublished = routine.status?.code === 'published' || routine.status?.name === 'PUBLICADO';

  const getFormattedExerciseName = (exEntry, exIdx) => {
    const baseName = exEntry.exercise?.title || exEntry.exercise_name || `Ejercicio ${exIdx + 1}`;
    const equipmentName = exEntry.equipment?.name || exEntry.exercise?.equipment?.name;
    
    return equipmentName ? `${baseName} - ${equipmentName}` : baseName;
  };

  return (
    <div style={{ padding: '16px 24px' }}>
      {/* Encabezado Superior */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
        <Col>
          <Space align="center">
            <Button icon={<ArrowLeftOutlined />} type="text" onClick={() => navigate(-1)} />
            <div>
              <Text type="secondary" style={{ cursor: 'pointer', color: '#1890ff' }} onClick={() => navigate(-1)}>Catálogo de Planes</Text>
              <Title level={3} style={{ margin: 0 }}>
                {routine.title}
              </Title>
            </div>
          </Space>
        </Col>
        <Col>
          <Space size="middle">
            <Tag color={isPublished ? 'green' : 'gold'}>
              {routine.status?.name || (isPublished ? 'PUBLICADO' : 'BORRADOR')}
            </Tag>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/routines/all')}>
              Volver
            </Button>
            {!isPublished && (
              <Button 
                type="primary" 
                icon={<EditOutlined />} 
                onClick={() => navigate(`/routines/${id}/edit`)}
              >
                Editar plan
              </Button>
            )}
          </Space>
        </Col>
      </Row>

      {/* Tarjeta de Información General */}
      <Card style={{ marginBottom: 24 }}>
        <Title level={4}>Detalles del plan</Title>
        <Row gutter={[24, 24]}>
          <Col xs={24} md={16}>
            <Descriptions bordered column={{ xs: 1, sm: 2 }}>
              <Descriptions.Item label="Nivel">
                {routine.level?.name || routine.level?.code || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Plan de Cobro / Precio">
                {routine.plan?.name || routine.plan?.code || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Enfoque Principal">
                {routine.focus?.wizard_label || routine.focus?.name || routine.focus?.code || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="¿Enfoque Especial?">
                {routine.is_special_focus ? (
                  <Tag color="purple">Sí</Tag>
                ) : (
                  <Tag>No</Tag>
                )}
              </Descriptions.Item>

              {routine.is_special_focus && (
                <Descriptions.Item label="Enfoques Especiales" span={2}>
                  <Space wrap>
                    {(routine.special_focuses || []).map((sf) => (
                      <Tag color="geekblue" key={sf.id}>
                        {sf.wizard_label || sf.name || sf.code}
                      </Tag>
                    ))}
                  </Space>
                </Descriptions.Item>
              )}

              <Descriptions.Item label="Duración Estimada">
                {routine.duration_config
                  ? `${routine.duration_config.value} ${routine.duration_config.unit === 'weeks' ? 'Semana(s)' : routine.duration_config.unit}`
                  : '—'}
              </Descriptions.Item>

              <Descriptions.Item label="Frecuencias Permitidas">
                <Space wrap>
                  {(routine.frequencies || []).map((freq) => (
                    <Tag key={freq.id} color="cyan">
                      {freq.name || `${freq.days} días`}
                    </Tag>
                  ))}
                </Space>
              </Descriptions.Item>

              <Descriptions.Item label="Contexto / Equipo" span={2}>
                <Space wrap>
                  {(routine.contexts || []).map((ctx, idx) => (
                    <Tag key={idx} color="blue">
                      {ctx.location?.name || ''} 
                      {ctx.equipment?.name ? ` — ${ctx.equipment?.name}` : ''}
                    </Tag>
                  ))}
                </Space>
              </Descriptions.Item>

              <Descriptions.Item label="Avatares Asignados" span={2}>
                <Space wrap>
                  {(routine.executors || []).map((exec) => (
                    <Tag key={exec.id} color="magenta">
                      {exec.name}
                    </Tag>
                  ))}
                </Space>
              </Descriptions.Item>

              <Descriptions.Item label="Descripción Corta" span={2}>
                <Text type="secondary">{routine.short_description || 'Sin descripción'}</Text>
              </Descriptions.Item>
            </Descriptions>
          </Col>

          <Col xs={24} md={8} style={{ textAlign: 'center' }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>Imagen de Portada</Text>
            {routine.preview_path ? (
              <Image
                src={resolveMedia(routine.preview_path)}
                alt="Portada de rutina"
                style={{
                  maxHeight: 220,
                  width: '100%',
                  objectFit: 'cover',
                  borderRadius: 8,
                  border: '1px solid #d9d9d9'
                }}
              />
            ) : (
              <div style={{
                height: 180,
                background: '#f5f5f5',
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px border-dashed #d9d9d9'
              }}>
                <Text type="secondary">Imagen predeterminada del sistema</Text>
              </div>
            )}
          </Col>
        </Row>
      </Card>

      {/* Estructura Semanal (Programación Visual) */}
      <Card title={<Title level={4} style={{ margin: 0 }}>Estructura Semanal</Title>}>
        {weeks.length === 0 ? (
          <Text type="secondary">Esta rutina no tiene semanas estructuradas o configuradas.</Text>
        ) : (
          weeks.map((week, weekIndex) => (
            <Collapse
              key={week.id || weekIndex}
              activeKey={expandedWeeks}
              onChange={(keys) => setExpandedWeeks(Array.isArray(keys) ? keys : [keys])}
              style={{ marginBottom: 16, backgroundColor: '#FAF9F6', borderColor: '#E5E1DA' }}
              items={[
                {
                  key: String(week.id || weekIndex + 1),
                  label: <Text strong style={{ fontSize: 16 }}>{week.title || `Semana ${weekIndex + 1}`}</Text>,
                  children: (
                    <Collapse
                      ghost
                      activeKey={expandedDays.filter((key) => key.startsWith(`${week.id || weekIndex + 1}::`))}
                      onChange={(keys) => {
                        const weekPrefix = `${week.id || weekIndex + 1}::`;
                        const nextKeys = Array.isArray(keys) ? keys : [keys];
                        setExpandedDays((prev) => [
                          ...prev.filter((key) => !key.startsWith(weekPrefix)),
                          ...nextKeys
                            .filter((key) => String(key).startsWith(weekPrefix))
                            .map(String)
                        ]);
                      }}
                      items={(week.days || []).map((day, dayIndex) => {
                        const dayKey = getPanelKey(week.id || weekIndex + 1, day.id || dayIndex);
                        const warmupRoutine = miniRoutinesCatalog.find(
                          (m) => m.id === (day.warm_up_id ?? day.warmup_routine_id)
                        );
                        const cooldownRoutine = miniRoutinesCatalog.find(
                          (m) => m.id === (day.stretch_id ?? day.cooldown_routine_id)
                        );

                        return {
                          key: dayKey,
                          label: (
                            <Space align="center" size="middle">
                              <Text strong style={{ minWidth: 90 }}>{day.title || `Día ${dayIndex + 1}`}</Text>
                              <Tag color={day.is_rest ? 'default' : 'blue'}>
                                {day.is_rest ? '💤 Descanso' : '🏋️ Entrenamiento'}
                              </Tag>
                              {!day.is_rest && (
                                <Text type="secondary">
                                  {day.session_name || 'Sin nombre de sesión'} — {(day.blocks || []).length} bloque(s)
                                </Text>
                              )}
                            </Space>
                          ),
                          children: !day.is_rest ? (
                            <div style={{ padding: '4px 8px 8px', borderLeft: '3px solid #1890ff' }}>
                              {/* Nombre de Sesión */}
                              {day.session_name && (
                                <div style={{ marginBottom: 12 }}>
                                  <Text type="secondary">Sesión: </Text>
                                  <Text strong>{day.session_name}</Text>
                                </div>
                              )}

                              {/* Calentamiento */}
                              {day.has_warmup && (
                                <Card size="small" style={{ marginBottom: 16, backgroundColor: '#FAFAFA' }}>
                                  <Text strong>🔥 Calentamiento</Text>
                                  <div style={{ marginTop: 4 }}>
                                    <Text type="secondary">Minirutina: </Text>
                                    <Text bold>{warmupRoutine?.title || day.warm_up_id || 'Asignada'}</Text>
                                  </div>
                                  {warmupRoutine?.exercises && (
                                    <div style={{ background: '#FFF', padding: 8, borderRadius: 4, marginTop: 8, border: '1px solid #E8E8E8' }}>
                                      <Text type="secondary" style={{ fontSize: 12 }}>Ejercicios incluidos:</Text>
                                      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
                                        {warmupRoutine.exercises.map((ex, idx) => (
                                          <li key={idx}>{ex.exercise?.name || ex.exercise?.title}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </Card>
                              )}

                              {/* Bloques de Ejercicios */}
                              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                                Bloques de Ejercicios:
                              </Text>

                              <Collapse
                                ghost
                                activeKey={expandedBlocks.filter((key) => 
                                  key.startsWith(`${week.id || weekIndex + 1}::${day.id || dayIndex}::`)
                                )}
                                onChange={(keys) => {
                                  const dayPrefix = `${week.id || weekIndex + 1}::${day.id || dayIndex}::`;
                                  const nextKeys = Array.isArray(keys) ? keys : [keys];
                                  setExpandedBlocks((prev) => [
                                    ...prev.filter((key) => !key.startsWith(dayPrefix)),
                                    ...nextKeys.filter((key) => String(key).startsWith(dayPrefix)).map(String)
                                  ]);
                                }}
                                items={(day.blocks || []).map((block, blockIdx) => {
                                  const blockKey = getPanelKey(week.id || weekIndex + 1, day.id || dayIndex, block.id || blockIdx);

                                  return {
                                    key: blockKey,
                                    label: (
                                      <Space>
                                        <Tag color="blue">{block.type || 'Lineal'}</Tag>
                                        <Text strong>{block.name || `BLOQUE ${blockIdx + 1}`}</Text>
                                        <Text type="secondary">
                                          ({(block.exercises || []).length} ejercicio{(block.exercises || []).length === 1 ? '' : 's'})
                                        </Text>
                                      </Space>
                                    ),
                                    children: (
                                      <>
                                        {(block.exercises || []).map((exEntry, exIdx) => (
                                          <Card
                                            key={exEntry.id || exIdx}
                                            size="small"
                                            style={{ marginBottom: 12, backgroundColor: '#FFFFFF' }}
                                            title={
                                              <Text strong>
                                                {exIdx + 1}. {getFormattedExerciseName(exEntry, exIdx)}
                                              </Text>
                                            }
                                          >
                                            <Space style={{ marginBottom: 12 }} size="large">
                                              <Text style={{ fontSize: 13 }}>
                                                <strong>Series:</strong> {exEntry.num_sets || exEntry.sets_config?.length || 0}
                                              </Text>
                                              <Text style={{ fontSize: 13 }}>
                                                <strong>Modalidad:</strong> {exEntry.is_equal_sets ? 'Series Iguales' : 'Series Variables'}
                                              </Text>
                                            </Space>

                                            {/* Configuración de Series Iguales */}
                                            {exEntry.is_equal_sets ? (
                                              <Row gutter={12} style={{ background: '#FAFAFA', padding: 12, borderRadius: 4 }}>
                                                <Col xs={8}>
                                                  <Text type="secondary" style={{ fontSize: 12 }}>Reps:</Text>
                                                  <div><Text strong>{exEntry.global_config?.reps ?? exEntry.sets_config?.[0]?.reps ?? '—'}</Text></div>
                                                </Col>
                                                <Col xs={8}>
                                                  <Text type="secondary" style={{ fontSize: 12 }}>Segundos:</Text>
                                                  <div><Text strong>{exEntry.global_config?.seconds ?? exEntry.sets_config?.[0]?.seconds ?? '—'}</Text></div>
                                                </Col>
                                                <Col xs={8}>
                                                  <Text type="secondary" style={{ fontSize: 12 }}>Peso (kg):</Text>
                                                  <div><Text strong>{exEntry.global_config?.weight ?? exEntry.sets_config?.[0]?.weight ?? '—'}</Text></div>
                                                </Col>
                                              </Row>
                                            ) : (
                                              /* Configuración de Series Distintas */
                                              <Table
                                                size="small"
                                                pagination={false}
                                                dataSource={exEntry.sets_config || []}
                                                rowKey={(s, i) => s.set_number || i}
                                                columns={[
                                                  {
                                                    title: 'Serie',
                                                    dataIndex: 'set_number',
                                                    width: 80,
                                                    render: (n, _, i) => <Text strong>#{n ?? i + 1}</Text>
                                                  },
                                                  {
                                                    title: 'Reps',
                                                    dataIndex: 'reps',
                                                    render: (v) => v ?? '—'
                                                  },
                                                  {
                                                    title: 'Segundos',
                                                    dataIndex: 'seconds',
                                                    render: (v) => v ?? '—'
                                                  },
                                                  {
                                                    title: 'Peso (kg)',
                                                    dataIndex: 'weight',
                                                    render: (v) => v ?? '—'
                                                  }
                                                ]}
                                              />
                                            )}
                                          </Card>
                                        ))}
                                      </>
                                    )
                                  };
                                })}
                              />

                              {/* Estiramiento / Cooldown */}
                              {day.has_cooldown && (
                                <Card size="small" style={{ marginTop: 16, backgroundColor: '#FAFAFA' }}>
                                  <Text strong>🧘 Estiramiento / Enfriamiento</Text>
                                  <div style={{ marginTop: 4 }}>
                                    <Text type="secondary">Minirutina: </Text>
                                    <Text bold>{cooldownRoutine?.title || day.stretch_id || 'Asignada'}</Text>
                                  </div>
                                  {cooldownRoutine?.exercises && (
                                    <div style={{ background: '#FFF', padding: 8, borderRadius: 4, marginTop: 8, border: '1px solid #E8E8E8' }}>
                                      <Text type="secondary" style={{ fontSize: 12 }}>Ejercicios incluidos:</Text>
                                      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
                                        {cooldownRoutine.exercises.map((ex, idx) => (
                                          <li key={idx}>{ex.exercise?.name || ex.exercise?.title}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </Card>
                              )}
                            </div>
                          ) : (
                            <div style={{ padding: '8px 16px', color: '#8c8c8c' }}>
                              Día configurado como descanso.
                            </div>
                          )
                        };
                      })}
                    />
                  )
                }
              ]}
            />
          ))
        )}
      </Card>
    </div>
  );
};

export default RoutineViewPage;